import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation, faHeartPulse, faFire, faShieldHalved,
  faLocationDot, faCheckCircle, faSpinner, faHistory
} from '@fortawesome/free-solid-svg-icons';
import { BASE_URL } from '../utils/constants';

/* ── helpers ── */
const getUserId = () => {
  try {
    const t = localStorage.getItem('token');
    if (!t) return null;
    return JSON.parse(atob(t.split('.')[1])).id_usuario;
  } catch { return null; }
};

const TIPOS = [
  { value: 'panico',    label: 'Pánico',            icon: faTriangleExclamation, color: 'bg-red-600',     hover: 'hover:bg-red-700',     ring: 'ring-red-400',     text: 'text-red-600',  badge: 'bg-red-100 text-red-700'   },
  { value: 'medica',    label: 'Emergencia Médica',  icon: faHeartPulse,          color: 'bg-orange-500',  hover: 'hover:bg-orange-600',  ring: 'ring-orange-300',  text: 'text-orange-500', badge: 'bg-orange-100 text-orange-700' },
  { value: 'incendio',  label: 'Incendio',           icon: faFire,                color: 'bg-amber-500',   hover: 'hover:bg-amber-600',   ring: 'ring-amber-300',   text: 'text-amber-600', badge: 'bg-amber-100 text-amber-700'   },
  { value: 'seguridad', label: 'Situación de Seguridad', icon: faShieldHalved,   color: 'bg-blue-700',    hover: 'hover:bg-blue-800',    ring: 'ring-blue-400',    text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700'     },
];

const tipoMeta = (value) => TIPOS.find(t => t.value === value) || TIPOS[0];

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const months = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return `${dt.getDate()} ${months[dt.getMonth()]} ${dt.getFullYear()} · ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
};

const ESTADO_BADGE = {
  activa:    'bg-red-100 text-red-700 border border-red-200',
  en_camino: 'bg-purple-100 text-purple-700 border border-purple-200',
  atendida:  'bg-yellow-100 text-yellow-700 border border-yellow-200',
  resuelta:  'bg-green-100 text-green-700 border border-green-200',
};

const ESTADO_LABEL = {
  activa:    'Activa',
  en_camino: 'Personal en camino',
  atendida:  'Siendo atendida',
  resuelta:  'Resuelta',
};

const Snack = ({ msg, type, onClose }) => {
  if (!msg) return null;
  const colors = { success: 'bg-green-600', error: 'bg-red-600', info: 'bg-blue-600' };
  return (
    <div className={`fixed top-5 right-5 z-50 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 ${colors[type] || colors.info}`}>
      <span>{msg}</span>
      <button onClick={onClose} className="font-bold">✕</button>
    </div>
  );
};

export default function EmergencyPage({ NavigationComponent }) {
  const userId = getUserId();
  const [selected, setSelected] = useState(null);
  const [descripcion, setDescripcion] = useState('');
  const [location, setLocation] = useState(null);
  const [locLoading, setLocLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [snack, setSnack] = useState({ msg: '', type: 'info' });

  const showSnack = (msg, type = 'info') => {
    setSnack({ msg, type });
    setTimeout(() => setSnack({ msg: '', type: 'info' }), 4000);
  };

  const loadHistory = useCallback(() => {
    if (!userId) return;
    axios.get(`${BASE_URL}/emergencias/user/${userId}`)
      .then(res => setHistory(res.data))
      .catch(() => {})
      .finally(() => setHistLoading(false));
  }, [userId]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Obtener ubicación GPS
  const getLocation = () => {
    if (!navigator.geolocation) {
      showSnack('Tu navegador no soporta geolocalización', 'error');
      return;
    }
    setLocLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocLoading(false);
        showSnack('📍 Ubicación capturada', 'success');
      },
      () => {
        setLocLoading(false);
        showSnack('No se pudo obtener la ubicación', 'error');
      },
      { timeout: 10000 }
    );
  };

  // Enviar emergencia
  const handleSend = async () => {
    if (!selected) {
      showSnack('Selecciona el tipo de emergencia', 'error');
      return;
    }
    if (!userId) {
      showSnack('Error: sesión no válida', 'error');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${BASE_URL}/emergencias`, {
        id_usuario: userId,
        tipo: selected,
        descripcion: descripcion.trim(),
        latitud: location?.lat || null,
        longitud: location?.lon || null,
      });
      showSnack('🚨 Emergencia reportada. El equipo de seguridad fue notificado.', 'success');
      setSelected(null);
      setDescripcion('');
      setLocation(null);
      loadHistory();
      // Cooldown de 30s para evitar spam
      setCooldown(30);
      const interval = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      showSnack(err.response?.data?.error || 'Error al reportar emergencia', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {NavigationComponent && <NavigationComponent />}
      <Snack msg={snack.msg} type={snack.type} onClose={() => setSnack({ msg: '' })} />

      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* Header */}
          <div className="bg-red-600 rounded-2xl p-6 text-white shadow-lg text-center">
            <FontAwesomeIcon icon={faTriangleExclamation} className="text-4xl mb-2 animate-pulse" />
            <h1 className="text-2xl font-bold">EMERGENCIAS</h1>
            <p className="text-red-100 text-sm mt-1">Reporta cualquier situación de emergencia. El equipo de seguridad será notificado de inmediato.</p>
          </div>

          {/* Panel de emergencia */}
          <div className="bg-white rounded-2xl shadow-md p-6 space-y-5">
            <h2 className="font-bold text-gray-700 text-lg">¿Qué tipo de emergencia tienes?</h2>

            {/* Botones de tipo */}
            <div className="grid grid-cols-2 gap-4">
              {TIPOS.map(t => (
                <button
                  key={t.value}
                  onClick={() => setSelected(selected === t.value ? null : t.value)}
                  className={`flex flex-col items-center justify-center gap-2 rounded-2xl p-5 text-white font-bold text-sm transition-all duration-200 shadow-md
                    ${t.color} ${t.hover}
                    ${selected === t.value ? `ring-4 ${t.ring} scale-105 shadow-xl` : 'opacity-85 hover:opacity-100 hover:scale-105'}
                  `}
                >
                  <FontAwesomeIcon icon={t.icon} className="text-3xl" />
                  {t.label}
                </button>
              ))}
            </div>

            {/* Descripción */}
            {selected && (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-600">Descripción adicional (opcional)</label>
                <textarea
                  value={descripcion}
                  onChange={e => setDescripcion(e.target.value)}
                  placeholder="Describe brevemente la situación..."
                  rows={3}
                  maxLength={500}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm resize-none focus:ring-2 focus:ring-red-300 focus:border-red-400 outline-none"
                />
                <span className="text-xs text-gray-400">{descripcion.length}/500</span>
              </div>
            )}

            {/* Ubicación GPS */}
            {selected && (
              <button
                onClick={getLocation}
                disabled={locLoading}
                className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50"
              >
                {locLoading
                  ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin" /> Obteniendo ubicación...</>
                  : location
                    ? <><FontAwesomeIcon icon={faLocationDot} className="text-green-600" /> Ubicación capturada ✓ (actualizar)</>
                    : <><FontAwesomeIcon icon={faLocationDot} /> Compartir mi ubicación GPS</>
                }
              </button>
            )}

            {/* Botón de envío */}
            <button
              onClick={handleSend}
              disabled={!selected || sending || cooldown > 0}
              className={`w-full py-4 rounded-2xl font-bold text-white text-lg transition-all duration-200 shadow-md
                ${!selected || sending || cooldown > 0
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 active:scale-95 shadow-red-200'}`}
            >
              {sending
                ? <><FontAwesomeIcon icon={faSpinner} className="animate-spin mr-2" />Enviando...</>
                : cooldown > 0
                  ? `Espera ${cooldown}s antes de enviar otra emergencia`
                  : '🚨 REPORTAR EMERGENCIA'}
            </button>
          </div>

          {/* Historial del usuario */}
          <div className="bg-white rounded-2xl shadow-md p-6">
            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon icon={faHistory} className="text-gray-500" />
              <h2 className="font-bold text-gray-700 text-lg">Mis emergencias recientes</h2>
            </div>

            {histLoading ? (
              <p className="text-gray-400 text-sm text-center py-4">Cargando...</p>
            ) : history.length === 0 ? (
              <div className="text-center py-8">
                <FontAwesomeIcon icon={faCheckCircle} className="text-4xl text-green-400 mb-2" />
                <p className="text-gray-400 text-sm">No tienes emergencias registradas</p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map(e => {
                  const meta = tipoMeta(e.tipo);
                  return (
                    <div key={e.id_emergencia} className="border border-gray-100 rounded-xl p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${meta.color} flex items-center justify-center text-white flex-shrink-0`}>
                            <FontAwesomeIcon icon={meta.icon} />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-800 text-sm">{meta.label}</p>
                            {e.descripcion && <p className="text-xs text-gray-500 mt-0.5">{e.descripcion}</p>}
                            <p className="text-xs text-gray-400 mt-1">{fmtDate(e.fecha_emergencia)}</p>
                            {e.atendido_por_nombre && (
                              <p className="text-xs text-green-600 mt-0.5">Atendida por: {e.atendido_por_nombre} {e.atendido_por_apellido}</p>
                            )}
                          </div>
                        </div>
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full flex-shrink-0 ${ESTADO_BADGE[e.estado]}`}>
                          {ESTADO_LABEL[e.estado] || e.estado}
                        </span>
                      </div>
                      {(e.latitud && e.longitud) && (
                        <a
                          href={`https://www.google.com/maps?q=${e.latitud},${e.longitud}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-2 flex items-center gap-1 text-xs text-blue-500 hover:underline"
                        >
                          <FontAwesomeIcon icon={faLocationDot} /> Ver ubicación en mapa
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
